import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface HorizontalScrollSectionProps {
    /** Section title */
    title: string;
    /** Optional subtitle */
    subtitle?: string;
    /** Items to render */
    children: React.ReactNode;
    /** Gap between items in pixels */
    gap?: number;
    /** Whether to show navigation arrows */
    showArrows?: boolean;
    /** Container padding (horizontal) */
    containerPadding?: number | { xs?: number; sm?: number; md?: number };
    /** Optional action button */
    actionButton?: React.ReactNode;
}

export const HorizontalScrollSection: React.FC<HorizontalScrollSectionProps> = ({
    title,
    subtitle,
    children,
    gap = 16,
    showArrows = true,
    containerPadding = { xs: 2, md: 14 },
    actionButton
}) => {
    const theme = useTheme();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Check scroll position and update arrow states
    const checkScrollPosition = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    useEffect(() => {
        checkScrollPosition();
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollPosition);
            // Check on resize too
            window.addEventListener('resize', checkScrollPosition);
            return () => {
                container.removeEventListener('scroll', checkScrollPosition);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [children]);

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const scrollAmount = container.clientWidth * 0.8;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };

    return (
        <Box sx={{ width: '100%', py: 4, }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: containerPadding,
                    mb: 2
                }}
            >
                <Box>
                    <Typography
                        variant='h5'
                        fontWeight='bold'
                        sx={{
                            color: theme.palette.text.primary,
                            mb: subtitle ? 0.5 : 0
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant='body2' color='text.secondary'>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {actionButton}
                    
                    {/* Navigation Arrows - Only on desktop */}
                    {showArrows && !isMobile && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                onClick={() => scroll('left')}
                                disabled={!canScrollLeft}
                                size='small'
                                sx={{
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.05)',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.2)'
                                            : 'rgba(0, 0, 0, 0.1)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'transparent',
                                        opacity: 0.3
                                    }
                                }}
                            >
                                <ChevronLeft />
                            </IconButton>
                            <IconButton
                                onClick={() => scroll('right')}
                                disabled={!canScrollRight}
                                size='small'
                                sx={{
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.05)',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.2)'
                                            : 'rgba(0, 0, 0, 0.1)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'transparent',
                                        opacity: 0.3
                                    }
                                }}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Scrollable Content with Fade Edges */}
            <Box
                sx={{
                    mx: containerPadding,
                    position: 'relative',
                    // Left fade gradient
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: { xs: 24, md: 100 },
                        background: `linear-gradient(to right, ${theme.palette.background.default}, transparent)`,
                        zIndex: 1,
                        pointerEvents: 'none',
                        opacity: canScrollLeft ? 1 : 0,
                        transition: 'opacity 0.2s ease'
                    },
                    // Right fade gradient
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: { xs: 24, md: 100 },
                        background: `linear-gradient(to left, ${theme.palette.background.default}, transparent)`,
                        zIndex: 1,
                        pointerEvents: 'none',
                        opacity: canScrollRight ? 1 : 0,
                        transition: 'opacity 0.2s ease'
                    }
                }}
            >
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'flex',
                        gap: `${gap}px`,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        px: containerPadding,
                        pb: 2,
                        // Hide scrollbar but keep functionality
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        // Scroll snap for items
                        '& > *': {
                            scrollSnapAlign: 'start',
                            flexShrink: 0
                        }
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default HorizontalScrollSection;
